import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
	DndContext,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
	type DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

export interface OrderingItem {
	id: string;
	label: string;
	index: number;
}

interface Props {
	open: boolean;
	title: string;
	description?: string;
	items: OrderingItem[];
	loading?: boolean;
	onClose: () => void;
	onConfirm: (items: OrderingItem[]) => void;
}

/**
 * Modal for ordering items
 * - displays the items
 * - allows to drag and drop the items
 * - allows to confirm the ordering
 */
export default function KlausurErstellerOrderingModal({
	open,
	title,
	description,
	items,
	loading,
	onClose,
	onConfirm,
}: Props) {
	const [localItems, setLocalItems] = useState<OrderingItem[]>(items);

	useEffect(() => {
		setLocalItems(items);
	}, [items]);

	/**
	 * Sensors for the drag and drop
	 * (this is a react-dnd hook)
	 */
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	/**
	 * Handle the drag end event
	 * @param event - The drag end event
	 */
	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;
		const oldIndex = localItems.findIndex((i) => i.id === String(active.id));
		const newIndex = localItems.findIndex((i) => i.id === String(over.id));
		if (oldIndex === -1 || newIndex === -1) return;
		const reordered = arrayMove([...localItems], oldIndex, newIndex).map(
			(item, idx) => ({ ...item, index: idx }),
		);
		setLocalItems(reordered);
	};

	/**
	 * Item ids for the sortable context
	 */
	const itemIds = useMemo(() => localItems.map((i) => i.id), [localItems]);

	return (
		<Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					{description ? (
						<DialogDescription>{description}</DialogDescription>
					) : null}
				</DialogHeader>

				<div className="max-h-[60vh] overflow-y-auto py-2">
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={itemIds}
							strategy={verticalListSortingStrategy}
						>
							<div className="space-y-1">
								{localItems.map((it) => (
									<SortableRow key={it.id} id={it.id} label={it.label} />
								))}
							</div>
						</SortableContext>
					</DndContext>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={!!loading}>
						Abbrechen
					</Button>
					<Button onClick={() => onConfirm(localItems)} disabled={!!loading}>
						Speichern
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

/**
 * Sortable row for the ordering modal
 * @param id - The id of the item
 * @param label - The label of the item
 */
function SortableRow({ id, label }: { id: string; label: string }) {
	const { attributes, listeners, setNodeRef, transform, transition } =
		useSortable({ id });
	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className="flex items-center gap-2 px-2 py-2 rounded border bg-white"
		>
			<div
				{...attributes}
				{...listeners}
				className="cursor-grab p-1 rounded hover:bg-muted opacity-70"
				title="Ziehen zum Verschieben"
			>
				<GripVertical className="h-4 w-4" />
			</div>
			<div className="text-sm truncate flex-1" title={label}>
				{label}
			</div>
		</div>
	);
}
